import Box from '@mui/material/Box';
import type { DealInsightsGeneralStatsApi } from '@openx/types/dealInsights';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDealInsightsContext } from '../../../../context/DealInsightsContext';
import { mockGeneralData } from '../../__mocks__/mockGeneralData';
import { GeneralDataCard } from './components/GeneralDataCard';
import { GeneralDataInfo } from './components/GeneralDataInfo';
import { StatsPanelSettings } from './components/StatsPanelSettings';
import type { StatsPanelConfig } from './types';
import { getStatsPanelLabel } from './types';
import {
  applyStatsPanelConfig,
  buildStatsPanelItems,
  ensureInventoryQualityPanelConfig,
  getDefaultStatsPanelConfig,
  loadStatsPanelConfig,
  saveStatsPanelConfig,
} from './utils';

export const GeneralData = () => {
  const { t } = useTranslation();
  const {
    dealInsights: { data, chartsLoadingState },
  } = useDealInsightsContext();

  const [panelConfig, setPanelConfig] = useState<StatsPanelConfig[]>(
    () => loadStatsPanelConfig() || getDefaultStatsPanelConfig()
  );

  useEffect(() => {
    setPanelConfig(prev => {
      const next = ensureInventoryQualityPanelConfig(prev);

      if (next === prev) {
        return prev;
      }

      saveStatsPanelConfig(next);
      return next;
    });
  }, []);

  const showLoadingIndicator = !!chartsLoadingState.general_stats || !data;
  const hasPreviousPeriod = !!data?.general_stats?.previous_period_calculated;

  const panelLabels = useMemo(() => {
    return panelConfig.reduce(
      (acc, config) => {
        acc[config.key] = getStatsPanelLabel(config.key, t).label;
        return acc;
      },
      {} as Record<keyof Omit<DealInsightsGeneralStatsApi, 'previous_period_calculated'>, string>
    );
  }, [panelConfig, t]);

  const renderItems = useMemo(() => {
    const stats = showLoadingIndicator ? mockGeneralData : data?.general_stats;
    const items = buildStatsPanelItems(stats, t);

    return applyStatsPanelConfig(items, panelConfig).filter(item => !item.hide);
  }, [showLoadingIndicator, data, t, panelConfig]);

  const handleConfigChange = useCallback((newConfig: StatsPanelConfig[]) => {
    setPanelConfig(newConfig);
    saveStatsPanelConfig(newConfig);
  }, []);

  return (
    <Box
      data-test="general-data-container"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        pb: 4,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: 4,
            flexWrap: 'wrap',
            flex: 1,
          }}
        >
          {renderItems.map((item, index) => (
            <GeneralDataCard
              key={item.key}
              title={item.label}
              value={item.value}
              prefix={item.prefix}
              suffix={item.suffix}
              multiplier={item.multiplier}
              change={item.change}
              changeLabel={item.changeLabel}
              isLoading={showLoadingIndicator}
              index={index}
            />
          ))}
        </Box>

        <StatsPanelSettings config={panelConfig} onConfigChange={handleConfigChange} panelLabels={panelLabels} />
      </Box>
      {renderItems.length > 0 && <GeneralDataInfo sx={{ opacity: hasPreviousPeriod ? 1 : 0 }} />}
    </Box>
  );
};
